import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { labelForPath } from '../layout/nav';
import { startScreenCapture, startLiveWatch, type MonitorPolicy } from '../lib/screen-capture';
import { useToast } from './Toast';
import { desktop } from '../lib/desktop';

interface MonitorState {
  /** أيّ وضعٍ تفرضه الشركة الآن — تقرؤه صفحةُ الإذن لتعرض ما يُطلب أو تقول إنّه لا شيء. */
  policy: MonitorPolicy | null;
  /** داخل تطبيق سطح المكتب: الإذنُ مرّةً على الجهاز والبدءُ تلقائيّ. */
  inDesktop: boolean;
  /** جلسة مشاركة الشاشة قائمة الآن. */
  active: boolean;
  /** الجلسة القائمة يملكها الكونسول (لتسجيل مكالماته)، فإيقافُها من صفحة الإذن يمرّ عبره. */
  external: boolean;
  start: () => Promise<void>;
  stop: () => void;
  /**
   * تبنّي مسار شاشةٍ منحه غيرُنا (كونسول المكالمات) بدل طلب واحدٍ جديد.
   *
   * فلا يُطلب من الموظف مشاركةُ شاشته مرّتين، ولا يُلتقط مرّتين: مركز المراقبة
   * واحدٌ، ومنطق الالتقاط والمشاهدة الحيّة يجريان هنا حصرًا على أيّ مسارٍ يصل.
   * `onExternalStop` يُنادى حين يوقف المستخدم المراقبةَ من الشريط، فيُنهي مالكُ
   * المسار جلسته هو الآخر.
   */
  attach: (stream: MediaStream, onExternalStop: () => void) => void;
  /** فكّ التبنّي دون إيقاف المسار — مالكُه (الكونسول) يتكفّل بإغلاقه. */
  detach: () => void;
}

const Ctx = createContext<MonitorState | null>(null);

export function useScreenMonitor(): MonitorState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useScreenMonitor خارج مزوّده');
  return ctx;
}

/**
 * مراقبة الشاشة على مستوى النظام كلّه.
 *
 * مركَّبٌ فوق الهيكل لا داخل شاشة، فتبقى الجلسة قائمةً مهما تنقّل الموظف بين
 * الأقسام — وهو الغرض: مراقبته «في كل نقطة في النظام» لا في مركز الاتصال
 * وحده. في المتصفّح يمنح إذنها من «إذن مراقبة الشاشة» في الإعدادات (المتصفح
 * يشترط ضغطةً في كلّ جلسة)، وفي تطبيق سطح المكتب يُمنح الإذنُ مرّةً على
 * الجهاز وتبدأ وحدها — وأيقونةُ التطبيق بجوار الساعة تعرض حالتها.
 *
 * وكلُّ لقطةٍ تُرفع باسم الشاشة التي كان عليها لحظةَ أُخذت — يُقرأ المسار من
 * `location` عند كل التقاطٍ لا يُثبَّت عند البدء.
 */
export function ScreenMonitorProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const location = useLocation();

  /* المسار الحاليّ في مرجعٍ حيّ، فيقرؤه الالتقاط دون إعادة تشغيله مع كل تنقّل. */
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const [active, setActive] = useState(false);
  const [external, setExternal] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const stopCaptureRef = useRef<() => void>(() => undefined);
  const stopWatchRef = useRef<() => void>(() => undefined);
  /* حين يوقف المستخدم من الشريط جلسةً مملوكةً للكونسول، يُنهيها الكونسول بهذا. */
  const externalStopRef = useRef<(() => void) | null>(null);
  const endedRef = useRef<() => void>(() => undefined);
  /*
   * أوقفها صاحبُها بيده — فلا يعيد التطبيقُ تشغيلها وحدَه حتى يُعاد فتحُه.
   * إعادةُ التشغيل خلف ظهر من ضغط «إيقاف» تجعل الزرّ كذبة.
   */
  const userStoppedRef = useRef(false);
  const autoTriedRef = useRef<string | null>(null);

  const policyQuery = useQuery({
    queryKey: ['cc', 'monitor-policy'],
    queryFn: () => api<{ callRecording: boolean } & MonitorPolicy>('/callcenter/monitoring/policy'),
    /* السياسة قد تتغيّر من الإعدادات — تُعاد قراءتها كل بضع دقائق. */
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });

  const policy: MonitorPolicy | null = policyQuery.data
    ? { mode: policyQuery.data.mode, screenshotEverySec: policyQuery.data.screenshotEverySec }
    : null;

  const meta = () => {
    const p = pathRef.current;
    return { path: p, label: labelForPath(p) };
  };

  /*
   * في تطبيق سطح المكتب لا يُلتقط إلّا حين تكون نافذةُ GoyOne هي المُركَّز
   * عليها: فتحُ تطبيقٍ آخر يُوقف اللقطات فلا تُصوَّر شاشةُ الموظّف خارج
   * النظام. وفي المتصفّح لا شرط — يلتقط ما تشاركه كالمعتاد.
   */
  const captureOpts = desktop ? { shouldCapture: () => document.hasFocus() } : {};

  /**
   * يُشغّل الالتقاط والمشاهدة الحيّة على مسارٍ ما — مصدرُه واحدٌ سواء طلبناه
   * نحن أو تبنّيناه من الكونسول. `owned` يحسم من يُغلق المسار عند الإيقاف.
   */
  const run = (stream: MediaStream, owned: boolean) => {
    streamRef.current = stream;
    /* المراقبة تملك المسار الآن، فتُبقيه حيًّا — الكونسول كان يُطفئه حين OFF. */
    stream.getVideoTracks().forEach((t) => (t.enabled = true));
    stopCaptureRef.current = policy
      ? startScreenCapture(stream, policy, meta, (m) => toast(m), captureOpts)
      : () => undefined;
    stopWatchRef.current = startLiveWatch(stream);

    /* من أنهى المشاركة من شريط المتصفح أنهى الجلسة. */
    const onEnded = () => endedRef.current();
    stream.getVideoTracks()[0]?.addEventListener('ended', onEnded);

    setActive(true);
    setExternal(!owned);
    void flags(true);
  };

  /**
   * إيقاف المراقبة. المسار المملوك يُغلَق هنا؛ والمتبنَّى يُترك لمالكه بعد
   * إبلاغه ليُنهي جلسته هو الآخر — فلا تبقى نصفُ جلسةٍ قائمة.
   */
  const teardown = () => {
    stopCaptureRef.current();
    stopWatchRef.current();
    stopCaptureRef.current = () => undefined;
    stopWatchRef.current = () => undefined;

    const wasExternal = external;
    const onExtStop = externalStopRef.current;
    externalStopRef.current = null;

    if (!wasExternal) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;

    setActive(false);
    setExternal(false);
    void flags(false);

    if (wasExternal) onExtStop?.();
  };
  endedRef.current = teardown;

  const start = async () => {
    userStoppedRef.current = false;
    if (streamRef.current || !policy || policy.mode === 'OFF') return;
    try {
      /*
       * في المتصفّح: مشاركةُ تبويب النظام وحدَه لا الشاشة كاملة.
       *
       * `preferCurrentTab` يقصر نافذةَ المشاركة في كروم على التبويب الحاليّ،
       * فلا يختار الموظّفُ سطحَ مكتبه ولا تطبيقًا آخر — تُلتقط صفحاتُ GoyOne
       * فقط. متصفّحٌ لا يدعمه يعود إلى الاختيار المعتاد. وفي تطبيق سطح
       * المكتب يتولّى المعالجُ اختيارَ نافذة النظام، فهذا لا يخصّه.
       */
      const constraints = {
        video: true,
        audio: false,
        ...(desktop ? {} : { preferCurrentTab: true }),
      } as MediaStreamConstraints;
      const display = await navigator.mediaDevices.getDisplayMedia(constraints);
      run(display, true);
      toast(
        policy.mode === 'SCREENSHOTS'
          ? `بدأت المراقبة — لقطة كل ${policy.screenshotEverySec} ثانية`
          : 'بدأت المراقبة — يُسجَّل فيديو الشاشة',
      );
    } catch {
      /* أغلق نافذة الاختيار أو رفض — لا خطأ يُصرخ به. */
      streamRef.current = null;
    }
  };

  const attach = (stream: MediaStream, onExternalStop: () => void) => {
    /* لا تبنّي فوق جلسةٍ قائمة: الكونسول يفتح شيفته مرّةً في المتصفّح الواحد. */
    if (streamRef.current) return;
    externalStopRef.current = onExternalStop;
    run(stream, false);
  };

  const detach = () => {
    if (!streamRef.current || !external) return;
    stopCaptureRef.current();
    stopWatchRef.current();
    stopCaptureRef.current = () => undefined;
    stopWatchRef.current = () => undefined;
    streamRef.current = null;
    externalStopRef.current = null;
    setActive(false);
    setExternal(false);
    void flags(false);
  };

  /* تُطفأ عند مغادرة النظام: المسارات لا تبقى مفتوحةً بعد إغلاق التبويب. */
  useEffect(() => () => teardown(), []);

  /*
   * تغيّرت السياسةُ من الإعدادات والجلسةُ قائمة — يُعاد بناءُ حلقة الالتقاط
   * وحدَها بالمسار نفسه، بلا نافذة مشاركةٍ جديدة.
   *
   * بدونه تبقى الجلسةُ على الفاصل القديم حتى تُعاد كلُّها: من غيّر «كلّ ١٠
   * ثوانٍ» إلى «كلّ ١٠ دقائق» يظلّ يُصوَّر كلَّ عشر ثوانٍ حتى يُغلق النظام
   * ويفتحه. والمسارُ يبقى — لا يُطلب الإذنُ من جديد.
   */
  useEffect(() => {
    if (!streamRef.current || !policy || policy.mode === 'OFF') return;
    stopCaptureRef.current();
    stopCaptureRef.current = startScreenCapture(
      streamRef.current,
      policy,
      meta,
      (m) => toast(m),
      captureOpts,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy?.mode, policy?.screenshotEverySec]);

  const stopByUser = () => {
    userStoppedRef.current = true;
    teardown();
  };

  /*
   * في تطبيق سطح المكتب: تبدأ المراقبةُ وحدها حين تطلبها السياسة.
   *
   * التطبيقُ يجيب طلبَ الشاشة بعد موافقةٍ محفوظةٍ على الجهاز، فلا نافذةَ
   * اختيارٍ ولا ضغطة — وأوّلَ مرّةٍ يسأل الموظّفَ موافقتَه بنافذةٍ منه.
   * وتُجرَّب مرّةً لكلّ وضعٍ للسياسة: رفضُ الموافقة لا يعيد السؤالَ في كلّ
   * إعادة رسم، وتغييرُ الإدارة للسياسة يسأل من جديد.
   */
  useEffect(() => {
    if (!desktop || !policy || policy.mode === 'OFF') return;
    if (active || userStoppedRef.current) return;
    const key = `${policy.mode}:${policy.screenshotEverySec}`;
    if (autoTriedRef.current === key) return;
    autoTriedRef.current = key;
    void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy?.mode, policy?.screenshotEverySec, active]);

  /* التطبيقُ يعرض حالتها في أيقونته بجوار الساعة. */
  useEffect(() => {
    desktop?.monitoringChanged(active);
  }, [active]);

  /* سحبُ الموافقة من أيقونة التطبيق يوقفها فورًا. */
  useEffect(() => {
    if (!desktop) return undefined;
    return desktop.onConsentRevoked(() => {
      userStoppedRef.current = true;
      /* من المرجع لا من إغلاق الرسم الأوّل: `teardown` هناك يرى حالةً قديمة. */
      endedRef.current();
      toast('سُحبت موافقة مراقبة الشاشة — توقّفت المشاركة.');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Ctx.Provider
      value={{
        policy,
        inDesktop: desktop !== null,
        active,
        external,
        start,
        stop: stopByUser,
        attach,
        detach,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

/** يبلّغ الخادم أن مراقبة الشاشة فعّالة الآن — يظهر للمشرف علمًا في اللوحة. */
function flags(monitorOn: boolean): Promise<unknown> {
  return api('/callcenter/presence/flags', { method: 'PATCH', body: { monitorOn } }).catch(
    () => undefined,
  );
}
